import { BusinessObjectBase } from './base';
import type { User } from './user';

export interface CorrespondenceItemData {
  readonly id: number;
  readonly subject?: string;
  readonly body?: string;
  readonly senderId: number;
  readonly sender?: User;
  readonly recipientId: number;
  readonly recipient?: User;
}

export interface CorrespondenceItem extends Readonly<CorrespondenceItemData> {}
export class CorrespondenceItem extends BusinessObjectBase {
  static readonly entityName = 'CorrespondenceItem' as const;
  static readonly plural = 'correspondence-items';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<CorrespondenceItem[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new CorrespondenceItem(data));
  }

  static async findOne(filter?: any): Promise<CorrespondenceItem | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new CorrespondenceItem(data) : null;
  }

  static async findById(id: number, filter?: any): Promise<CorrespondenceItem> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new CorrespondenceItem(data);
  }

  static async create(
    data: Omit<CorrespondenceItemData, 'id'>,
  ): Promise<CorrespondenceItem> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new CorrespondenceItem(result);
  }

  static async updateById(
    id: number,
    data: Partial<CorrespondenceItemData>,
  ): Promise<CorrespondenceItem> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new CorrespondenceItem(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
